/**
 * PATH: apps/hr/src/pages/support/ticket-detail/TicketSidebar.tsx
 * PURPOSE: Sidebar panels for ticket details, people, linked employee, tagged workers, actions
 */

import { useNavigate } from 'react-router-dom'
import { CheckCircle2, AlertCircle, Clock, XCircle, ChevronDown, Users } from 'lucide-react'
import { cn, formatDate } from '../../../lib/utils'
import type { ApiTicket } from './types'
import type { TicketStatus } from '../../../types'

const STATUS_OPTIONS: { value: TicketStatus; label: string; icon: React.ElementType; color: string }[] = [
  { value: 'open', label: 'Open', icon: AlertCircle, color: 'text-blue-600' },
  { value: 'in_progress', label: 'In Progress', icon: Clock, color: 'text-amber-600' },
  { value: 'pending', label: 'Pending', icon: Clock, color: 'text-purple-600' },
  { value: 'resolved', label: 'Resolved', icon: CheckCircle2, color: 'text-emerald-600' },
  { value: 'closed', label: 'Closed', icon: XCircle, color: 'text-slate-500' },
]

interface TicketSidebarProps {
  ticket: ApiTicket
  showStatusDropdown: boolean
  setShowStatusDropdown: (v: boolean) => void
  onStatusChange: (status: TicketStatus) => void
  onDelete: () => void
  isUpdating: boolean
}

export function TicketSidebar({ ticket, showStatusDropdown, setShowStatusDropdown, onStatusChange, onDelete, isUpdating }: TicketSidebarProps) {
  const navigate = useNavigate()
  const employeeName = ticket.linked_employee ? `${ticket.linked_employee.first_name} ${ticket.linked_employee.last_name}` : null

  return (
    <div className="space-y-4">
      {/* Details */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Details</h3>
        <dl className="space-y-3">
          <div className="flex items-center justify-between"><dt className="text-sm text-slate-500">Type</dt><dd className="text-sm font-medium text-slate-900 dark:text-white capitalize">{ticket.ticket_type.replace(/_/g, ' ')}</dd></div>
          <div className="flex items-center justify-between"><dt className="text-sm text-slate-500">Created</dt><dd className="text-sm text-slate-900 dark:text-white">{formatDate(ticket.created_at, { day: 'numeric', month: 'short', year: 'numeric' })}</dd></div>
          {ticket.due_date && <div className="flex items-center justify-between"><dt className="text-sm text-slate-500">Due Date</dt><dd className={cn('text-sm font-medium', new Date(ticket.due_date) < new Date() ? 'text-red-600' : 'text-slate-900 dark:text-white')}>{formatDate(ticket.due_date, { day: 'numeric', month: 'short', year: 'numeric' })}</dd></div>}
        </dl>
      </div>

      {/* People */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-4">People</h3>
        <div className="space-y-3">
          {ticket.created_by && <div className="flex items-center justify-between"><span className="text-sm text-slate-500">Created by</span><div className="flex items-center gap-2"><div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-xs font-medium">{ticket.created_by.full_name.split(' ').map(n => n[0]).join('').substring(0, 2)}</div><span className="text-sm text-slate-900 dark:text-white">{ticket.created_by.full_name}</span></div></div>}
          {ticket.assignee && <div className="flex items-center justify-between"><span className="text-sm text-slate-500">Assigned to</span><div className="flex items-center gap-2"><div className="w-6 h-6 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-xs font-medium text-teal-700 dark:text-teal-400">{ticket.assignee.full_name.split(' ').map(n => n[0]).join('').substring(0, 2)}</div><span className="text-sm text-slate-900 dark:text-white">{ticket.assignee.full_name}</span></div></div>}
        </div>
      </div>

      {employeeName && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Linked Employee</h3>
          <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800" onClick={() => navigate(`/employees/${ticket.linked_employee_id}`)}>
            <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-sm font-medium">{employeeName.split(' ').map(n => n[0]).join('').substring(0, 2)}</div>
            <div><p className="text-sm font-medium text-slate-900 dark:text-white">{employeeName}</p><p className="text-xs text-slate-500">View profile</p></div>
          </div>
        </div>
      )}

      {ticket.tagged_workers && ticket.tagged_workers.length > 0 && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
          <div className="flex items-center gap-2 mb-3"><Users className="w-4 h-4 text-teal-600" /><h3 className="font-semibold text-slate-900 dark:text-white">Tagged Workers ({ticket.tagged_workers.length})</h3></div>
          <div className="space-y-2">
            {ticket.tagged_workers.map(worker => (
              <div key={worker.id} className="flex items-center gap-3 p-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800" onClick={() => navigate(`/employees/${worker.id}`)}>
                <div className="w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-xs font-medium text-teal-700 dark:text-teal-400">{worker.first_name[0]}{worker.last_name[0]}</div>
                <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-900 dark:text-white truncate">{worker.first_name} {worker.last_name}</p>{worker.email && <p className="text-xs text-slate-500 truncate">{worker.email}</p>}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-2">Tagged via @mentions in comments</p>
        </div>
      )}

      {/* Actions */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Actions</h3>
        <div className="space-y-2">
          <div className="relative">
            <button onClick={() => setShowStatusDropdown(!showStatusDropdown)} className="w-full px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 text-left flex items-center justify-between"><span>Change Status</span><ChevronDown className={cn('w-4 h-4 transition-transform', showStatusDropdown && 'rotate-180')} /></button>
            {showStatusDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 z-10 overflow-hidden">
                {STATUS_OPTIONS.map(({ value, label, icon: Icon, color }) => (
                  <button key={value} onClick={() => onStatusChange(value)} disabled={ticket.status === value || isUpdating} className={cn('w-full px-4 py-2 text-sm text-left flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-700', ticket.status === value && 'bg-slate-100 dark:bg-slate-700', 'disabled:opacity-50')}>
                    <Icon className={cn('w-4 h-4', color)} />{label}{ticket.status === value && <span className="ml-auto text-xs text-slate-400">(current)</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={onDelete} className="w-full px-4 py-2 text-sm font-medium text-red-600 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-left">Delete Ticket</button>
        </div>
      </div>
    </div>
  )
}
